import React, { FC, PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';

export type MenuContextProps = {
    /**
     * This parameter specifies whether the component should be visible to the user.
     */
    visible: boolean;
    /**
     * The value of this parameter is used in the rendering of the component; it is a reference to the control element, where the screen coordinates will be used as a reference for displaying the menu.
     */
    anchorEl?: HTMLElement;
    /**
     * This is an auxiliary function for controlling the visibility of the component.
     */
    setVisibility: (value: boolean, anchorEl?: HTMLElement) => void;
};

export const MenuContext = createContext<MenuContextProps | undefined>(undefined);

export const useMenuDropdown = (): MenuContextProps => {
    const ctx = useContext(MenuContext);

    return ctx as MenuContextProps;
};

const MenuProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
    const [anchorEl, setAnchorEl] = useState<undefined | HTMLElement>(undefined);
    const [isOpen, setIsOpenState] = useState<boolean>(false);

    const setVisibility = (value: boolean, anchorElementRef?: HTMLElement) => {
        setIsOpenState(value);
        setAnchorEl(anchorElementRef);
    };

    const valueMemorized = useMemo(() => {
        return { visible: isOpen, anchorEl, setVisibility };
    }, [anchorEl, isOpen]);

    return <MenuContext.Provider value={valueMemorized}>{children}</MenuContext.Provider>;
};

export default MenuProvider;
